import type { TranslationDictionaryType } from "@shared/types/i18n.interface";

export const en = {
  "common.toggle": "Toggle",
  "common.retry": "Retry",
  "common.save": "Save",
  "common.saveChanges": "Save Changes",
  "common.saving": "Saving...",
  "common.cancel": "Cancel",
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.close": "Close",
  "common.dismiss": "Dismiss",
  "common.back": "Back",
  "common.next": "Next",
  "common.submit": "Submit",
  "common.loading": "Loading...",
  "common.search": "Search",
  "common.actions": "Actions",
  "common.view": "View",
  "common.showRawJson": "Show raw JSON",

  "status.enabled": "Enabled",
  "status.disabled": "Disabled",
  "status.active": "Active",
  "status.inactive": "Inactive",
  "status.saved": "Saved",
  "status.default": "Default",

  "common.featureNotImplemented.title": "Coming Soon",
  "common.featureNotImplemented.description":
    "This feature is currently under development. Check back soon!",

  "common.unauthorized.title": "Access Denied",
  "common.unauthorized.message":
    "You don't have permission to access this page.",

  "errors.generic": "An error occurred. Please try again.",
  "errors.network": "Unable to connect. Please check your connection.",
  "errors.notFound": "The requested {{resource}} was not found.",
  "errors.serverError": "Server error. Please try again later.",
  "errors.validationFailed": "Please check the form for errors.",
  "errors.unknown": "Unknown error",

  "auth.otp.title.authenticating": "Authenticating",
  "auth.otp.title.failed": "Authentication Failed",
  "auth.otp.verifying": "Verifying your authentication...",
  "auth.otp.redirecting": "Authentication successful. Redirecting...",
  "auth.otp.error.noToken": "No authentication token provided.",
  "auth.otp.error.invalid":
    "This authentication link is invalid or has expired. Please request a new link to continue.",
  "auth.otp.error.generic": "Authentication failed. Please try again.",
  "auth.otp.expired.title": "Request a new link",
  "auth.otp.expired.message":
    "For your security, please return to the chat and request it again.",

  "magicLink.title.processing": "Processing your link...",
  "magicLink.error.noId": "No link provided.",
  "magicLink.error.invalid":
    "This link is invalid or has expired. Please request a new link to continue.",
  "magicLink.error.generic": "Failed to process link. Please try again.",

  "login.title": "Welcome back",
  "login.subtitle": "Sign in to continue to Planta.",
  "login.email.label": "Email",
  "login.email.placeholder": "Enter your Email",
  "login.password.label": "Password",
  "login.password.placeholder": "Enter your Password",
  "login.submit": "Sign in",
  "login.submitting": "Signing in...",
  "login.orSeparator": "or",
  "login.error.invalidCredentials":
    "Invalid credentials. Please check your Email and Password.",
  "login.error.serverError": "Server error. Please try again later.",
  "login.error.networkError":
    "Network error. Please check your connection and try again.",
  "login.error.failed": "Login failed (error {{code}}). Please try again.",
  "login.error.generic": "An unexpected error occurred. Please try again.",
  "login.signInWithGoogle": "Sign in with Google",

  "auth.oauth.title.completing": "Completing sign-in...",
  "auth.oauth.error.title": "Sign-in failed",
  "auth.oauth.error.missingParams":
    "The sign-in link is missing required information. Please try signing in again.",
  "auth.oauth.error.invalidState":
    "This sign-in link is invalid or has expired. Please try signing in again.",
  "auth.oauth.error.exchangeFailed":
    "We couldn't complete the sign-in with the identity provider. Please try again.",
  "auth.oauth.error.emailNotVerified":
    "Your email is not verified with the identity provider. Please verify your email and try again.",
  "auth.oauth.error.unsupportedSource": "This sign-in method is not supported.",
  "auth.oauth.error.authMethodMismatch":
    "This account was created with a different sign-in method. Please use the original method to sign in.",
  "auth.oauth.error.signupNotAllowed":
    "Sign-up via this identity provider is not available for this email.",
  "auth.oauth.error.generic":
    "Something went wrong during sign-in. Please try again.",
  "auth.oauth.backToLogin": "Back to login",

  "common.timezones.america.anchorage": "Alaska Time - Anchorage",
  "common.timezones.america.losAngeles": "Pacific Time - Los Angeles",
  "common.timezones.america.vancouver": "Pacific Time - Vancouver",
  "common.timezones.america.denver": "Mountain Time - Denver",
  "common.timezones.america.phoenix": "Mountain Time - Phoenix",
  "common.timezones.america.chicago": "Central Time - Chicago",
  "common.timezones.america.mexicoCity": "Central Time - Mexico City",
  "common.timezones.america.newYork": "Eastern Time - New York",
  "common.timezones.america.toronto": "Eastern Time - Toronto",
  "common.timezones.america.bogota": "Colombia Time - Bogota",
  "common.timezones.america.lima": "Peru Time - Lima",
  "common.timezones.america.caracas": "Venezuela Time - Caracas",
  "common.timezones.america.santiago": "Chile Time - Santiago",
  "common.timezones.america.saoPaulo": "Brasilia Time - Sao Paulo",
  "common.timezones.america.buenosAires": "Argentina Time - Buenos Aires",

  "common.timezones.atlantic.reykjavik": "Iceland Time - Reykjavik",
  "common.timezones.europe.london": "UK Time - London",
  "common.timezones.europe.dublin": "Ireland Time - Dublin",
  "common.timezones.europe.lisbon": "Portugal Time - Lisbon",
  "common.timezones.europe.paris": "Central European Time - Paris",
  "common.timezones.europe.berlin": "Central European Time - Berlin",
  "common.timezones.europe.madrid": "Central European Time - Madrid",
  "common.timezones.europe.rome": "Central European Time - Rome",
  "common.timezones.europe.amsterdam": "Central European Time - Amsterdam",
  "common.timezones.europe.brussels": "Central European Time - Brussels",
  "common.timezones.europe.stockholm": "Central European Time - Stockholm",
  "common.timezones.europe.warsaw": "Central European Time - Warsaw",
  "common.timezones.europe.athens": "Eastern European Time - Athens",
  "common.timezones.europe.helsinki": "Eastern European Time - Helsinki",
  "common.timezones.europe.istanbul": "Turkey Time - Istanbul",
  "common.timezones.europe.moscow": "Moscow Time - Moscow",

  "common.timezones.africa.cairo": "Egypt Time - Cairo",
  "common.timezones.africa.johannesburg": "South Africa Time - Johannesburg",
  "common.timezones.asia.jerusalem": "Israel Time - Jerusalem",
  "common.timezones.asia.dubai": "Gulf Time - Dubai",

  "common.timezones.asia.kolkata": "India Time - Kolkata",
  "common.timezones.asia.dhaka": "Bangladesh Time - Dhaka",
  "common.timezones.asia.bangkok": "Indochina Time - Bangkok",
  "common.timezones.asia.jakarta": "Indonesia Time - Jakarta",
  "common.timezones.asia.shanghai": "China Time - Shanghai",
  "common.timezones.asia.hongKong": "Hong Kong Time",
  "common.timezones.asia.singapore": "Singapore Time",
  "common.timezones.asia.taipei": "Taiwan Time - Taipei",
  "common.timezones.asia.seoul": "Korea Time - Seoul",
  "common.timezones.asia.tokyo": "Japan Time - Tokyo",

  "common.timezones.australia.perth": "Western Australia - Perth",
  "common.timezones.australia.adelaide": "Central Australia - Adelaide",
  "common.timezones.australia.brisbane": "Eastern Australia - Brisbane",
  "common.timezones.australia.sydney": "Eastern Australia - Sydney",
  "common.timezones.australia.melbourne": "Eastern Australia - Melbourne",
  "common.timezones.pacific.auckland": "New Zealand Time - Auckland",

  "common.timezones.utc": "Coordinated Universal Time (UTC)",

  // Schema-driven form (PLA-676): widgets + DefaultValuePanel.

  // sites don't shift; the keys themselves are widget-generic.
  "featureConfig.form.schemaForm.defaults": "Defaults",
  "featureConfig.form.schemaForm.defaultsEmpty": "(no editable fields)",
  "featureConfig.form.schemaForm.defaultsEmptyList": "(empty list)",
  "featureConfig.form.schemaForm.defaultsEmptyMap": "(empty map)",
  "featureConfig.form.schemaForm.noDefault": "(no default)",
  "featureConfig.form.schemaForm.placeholder.email": "email address",
  "featureConfig.form.schemaForm.placeholder.url": "https://...",
  "featureConfig.form.schemaForm.placeholder.regex": "must match pattern",
  "featureConfig.form.schemaForm.list.add": "Add",
  "featureConfig.form.schemaForm.list.remove": "Remove",
  "featureConfig.form.schemaForm.object.add": "Add section",
  "featureConfig.form.schemaForm.object.remove": "Remove section",
  "featureConfig.form.schemaForm.record.add": "Add row",
  "featureConfig.form.schemaForm.record.remove": "Remove",
  "featureConfig.form.schemaForm.record.keyPlaceholder": "key",
  "featureConfig.form.schemaForm.record.valuePlaceholder": "value",
  "featureConfig.form.schemaForm.record.duplicateKeyError":
    "Key already exists — pick a different one",

  "participantProfile.topPicks.overrideBadge": "Host suggestion",
  "participantProfile.aiSummary": "Porter's read.",
  "participantProfile.linkedin.title": "LinkedIn summary",
  "participantProfile.linkedin.notAvailable": "Nothing from LinkedIn yet",
  "participantProfile.linkedin.notAvailableDescription":
    "When they add their LinkedIn, what they have written about themselves shows up here.",
  "participantProfile.linkedin.notAvailableDescriptionOwn":
    "When you add your LinkedIn, what you have written about yourself shows up here.",
  "participantProfile.photo.enlarge": "View full photo",
  "participantProfile.photo.closePreview": "Close photo preview",
} as const satisfies TranslationDictionaryType;

declare module "@shared/types/translation-key.types" {
  interface TranslationKeyRegistry {
    frontendShared: keyof typeof en;
  }
}
